/**
 * @file: useTaskInspection.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

/* eslint-disable @typescript-eslint/no-use-before-define */
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import {
  buildInspectionFlowSummary,
  collectInspectionFlowSummary,
  customIssueToStaged,
} from '@euler/app/flows/task/functions/task-detail/util';
import { useViewReportWithTaskContext } from '@euler/app/flows/task/functions/useViewReportWithTaskContext';
import { useAppLoading } from '@euler/components/loading/Provider';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { InspectionSiteInfo } from '@euler/model';
import { OrgMember } from '@euler/model/entity';
import { InspectionCategory, SiteInspectionType } from '@euler/model/enum';
import { CustomIssueItemStaged } from '@euler/model/task-detail';
import { delayed, sleep } from '@euler/utils';
import { uniq } from '@euler/utils/array';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useActionSheet } from '@expo/react-native-action-sheet';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { SectionList, View, findNodeHandle } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { useSiteInspectionNavigatorContext } from '../site-inspection/SiteInspectionContext';
import * as InspectionSiteGrid from './InspectionSiteGrid';
import { useInspectionSiteGrid } from './useInspectionSiteGrid';

export type TaskInspectionProps = {
  flowId: string;
  onCancel: (flowId: string) => void;
};

export const useTaskInspection = ({
  flowId,
  onCancel: onCancelFlow,
}: TaskInspectionProps & {
  mode: 'bottomSheet' | 'sidePanel';
}) => {
  const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
  const appLoading = useAppLoading();
  const alert = useAlert();
  const confirm = useConfirm();

  const { preventNavigateAway$, siteInspectionFlow } =
    useSiteInspectionNavigatorContext();
  const [preventNavigateAway] = useBehaviorSubject(preventNavigateAway$);

  const { detail, inventoryQuery, taskManager, storeConfiguration, profile } =
    useTaskContext();
  const { user } = taskManager.currentUser;
  const flow = detail.inspection.flows.find(x => x.id === flowId);

  const listRef = useRef<SectionList>(null);
  const [isMemberListVisible, setIsMemberListVisible] = useState(false);

  const presentedSiteIdRef = useRef<number>();

  const [keyword, onKeywordChange] = useState('');

  const {
    sections,
    scrollerItems,
    activeSectionForHiddenSites,
    setActiveSectionForHiddenSites,
  } = useInspectionSiteGrid({ flowId, keyword });

  const presentSiteInspectionFlow = useCallback(
    (item: InspectionSiteGrid.ItemInfo) => {
      const siteId = item.siteId;
      const site = inventoryQuery.getSiteById(siteId);
      if (!site) return;
      siteInspectionFlow.current?.present(
        '_siteInspection',
        {
          flowId,
          site,
          mediaCaptureRequired: item.mediaCaptureRequired,
          onDone: () => {
            siteInspectionFlow.current?.finish();
          },
        },
        site.code,
      );
    },
    [inventoryQuery, siteInspectionFlow, flowId],
  );

  const presentCustomIssueScreen = usePersistFn(
    (customIssueStaged?: CustomIssueItemStaged) => {
      siteInspectionFlow.current?.present('_customIssue', {
        flowId,
        siteId: customIssueStaged?.siteId,
        category: InspectionCategory.Normal,
        inspectionType: SiteInspectionType.Default,
        customIssue: customIssueStaged,
        showMedias: true,
        onDone: async customIssueInspectionManager => {
          if (flow == null) return;
          await appLoading.showWithAction(async () => {
            await customIssueInspectionManager.commit(flow, inventoryQuery);
            siteInspectionFlow.current?.finish();
          });
        },
        onDelete: async customIssueInspectionManager => {
          siteInspectionFlow.current?.finish();
          await sleep(500);
          await appLoading.showWithAction(async () => {
            await customIssueInspectionManager.deleteCustomIssue();
          });
        },
      });
    },
  );

  const onSiteSelected = useCallback(
    async (item: InspectionSiteGrid.ItemInfo) => {
      const doPresentSiteInspectionFlow = () => {
        if (item.itemType === 'site') {
          presentSiteInspectionFlow(item);
          presentedSiteIdRef.current = item.siteId;
        } else {
          if (!item.customIssueId) return;
          const customIssue = detail.inspection.customIssues?.find(
            x => x.id === item.customIssueId,
          );
          if (!customIssue) return;
          const customIssueStaged = customIssueToStaged(customIssue);
          presentCustomIssueScreen(customIssueStaged);
        }
      };

      if (presentedSiteIdRef.current === item.siteId) {
        return;
      }

      if (preventNavigateAway && presentedSiteIdRef.current != null) {
        const confirmed = await confirm({
          title: '确认',
          message:
            '您即将切换当前正在检测的部位，切换后您所填写的信息将丢失且不可撤销，是否继续?',
          cancelButtonText: '否',
          confirmButtonText: '是',
        });
        if (confirmed) {
          preventNavigateAway$.next(false);
          taskManager.inspectionManager.endSiteInspection(
            presentedSiteIdRef.current,
          );
          delayed(() => {
            doPresentSiteInspectionFlow();
          }, 0);
        }
        return;
      }

      doPresentSiteInspectionFlow();
    },
    [
      confirm,
      detail.inspection.customIssues,
      presentCustomIssueScreen,
      presentSiteInspectionFlow,
      preventNavigateAway,
      preventNavigateAway$,
      taskManager.inspectionManager,
    ],
  );

  const onSiteDismissed = useCallback(() => {
    presentedSiteIdRef.current = undefined;
  }, []);

  const onHiddenSiteSelected = useCallback(
    (item: InspectionSiteGrid.ItemInfo) => {
      setActiveSectionForHiddenSites(undefined);
      presentSiteInspectionFlow(item);
    },
    [presentSiteInspectionFlow, setActiveSectionForHiddenSites],
  );

  const onScrollerItemPress = useCallback((index: number) => {
    listRef.current?.scrollToLocation({ sectionIndex: index, itemIndex: 0 });
  }, []);

  const onSearchInputPress = useCallback(() => {
    const sites = uniq(
      sections.flatMap(x => [...x.data[0].items, ...(x.hiddenSites ?? [])]),
      x => x.siteId,
    );
    navigation.push('_inspectionSiteSearch', {
      sites,
      onSelect: item => {
        navigation.goBack();
        void onSiteSelected(item);
      },
    });
  }, [navigation, onSiteSelected, sections]);

  const scrollY = useSharedValue(0);

  const onScroll = useCallback(
    (value: number) => {
      'worklet';
      scrollY.value = value;
    },
    [scrollY],
  );

  const canFinish = usePersistFn(() => {
    if (!flow) return false;

    const template = flow.template;

    const inspectedSiteIdSet = new Set(
      detail.inspection.sites.map(x => x.siteId),
    );

    const siteIdSetInTemplate = new Set([
      ...sections.flatMap(section =>
        section.data.flatMap(list => list.items.flatMap(x => x.siteId)),
      ),
      ...sections.flatMap(section =>
        (section.hiddenSites ?? []).map(x => x.siteId),
      ),
    ]);

    const requiredButNotInspectedSites: InspectionSiteInfo[] = [];

    for (const category of template.conf.categories) {
      for (const group of category.groups) {
        if (!group.requiredSiteIds?.length) continue;

        for (const requiredSiteId of group.requiredSiteIds) {
          // skip site if not presented in template group.
          if (!group.siteIds.includes(requiredSiteId)) {
            __DEV__ &&
              console.log(
                `site #${requiredSiteId} is not presented in template group ${group.name}`,
              );
            continue;
          }

          // skip site if not presented in template.
          if (!siteIdSetInTemplate.has(requiredSiteId)) {
            __DEV__ &&
              console.log(
                `site #${requiredSiteId} is not presented in template any more`,
              );
            continue;
          }

          // skip if the site does not exist any more.
          const site = inventoryQuery.getSiteById(requiredSiteId);
          if (!site) {
            __DEV__ && console.log(`site #${requiredSiteId} does not exist`);
            continue;
          }

          if (!inspectedSiteIdSet.has(requiredSiteId)) {
            __DEV__ &&
              console.log(
                `site #${requiredSiteId} (${site.name}) is not inspected yet`,
              );
            if (!requiredButNotInspectedSites.some(x => x.id === site.id)) {
              requiredButNotInspectedSites.push(site);
            }
          }
        }
      }
    }

    if (requiredButNotInspectedSites.length) {
      void alert({
        title: '提示',
        message:
          '请完成以下必检项目然后重试!' +
          requiredButNotInspectedSites.map(x => x.name).join(', '),
      });
      return false;
    }

    return true;
  });

  const onFinish = usePersistFn(async () => {
    const summary = buildInspectionFlowSummary(s => {
      detail.inspection.sites
        .filter(x => !x.flowId || x.flowId === flowId)
        .forEach(x => {
          collectInspectionFlowSummary(s, x.items);
        });

      (
        detail.inspection.customIssues?.filter(
          x => !x.flowId || x.flowId === flowId,
        ) ?? []
      ).forEach(x => {
        collectInspectionFlowSummary(s, [x]);
      });
    });

    await taskManager.inspectionManager.finishInspectionFlow(flowId, summary, {
      technicianId: user.memberId,
      technicianName: user.name,
    });
  });

  const viewReport = useViewReportWithTaskContext();

  const onPreview = useCallback(() => {
    viewReport('inspectionReport', {
      flowId,
      canFinish,
      onFinish,
    });
  }, [canFinish, flowId, onFinish, viewReport]);

  const onCancel = usePersistFn(async () => {
    if (storeConfiguration.restrictedRolesForCancelInspectionFlow?.length) {
      const roleHelper = new OrgUserRoleHelper(profile.user.role);
      if (
        !roleHelper.matchesAny(
          storeConfiguration.restrictedRolesForCancelInspectionFlow,
        )
      ) {
        void alert({
          title: '您没有权限取消检测流程',
          message: '若需帮助，请联系门店系统管理员！',
        });
        return;
      }
    }

    if (
      !(await confirm({
        title: '确认取消检测流程',
        message: '您即将取消当前检测流程，该操作不可撤销，是否确认继续取消?',
      }))
    ) {
      return;
    }
    onCancelFlow?.(flowId);
  });

  const { showActionSheetWithOptions } = useActionSheet();

  const onMorePress = usePersistFn((anchor?: RefObject<View>) => {
    let nodeId: number | null = null;
    if (anchor?.current != null) {
      nodeId = findNodeHandle(anchor.current);
    }
    showActionSheetWithOptions(
      {
        options: ['添加自定义故障', '取消当前检测流程', '关闭'],
        anchor: nodeId ?? undefined,
        destructiveButtonIndex: 1,
        cancelButtonIndex: 2,
        useModal: false,
      },
      async buttonIndex => {
        if (buttonIndex === 0) {
          presentCustomIssueScreen();
        } else if (buttonIndex === 1) {
          await onCancel();
        }
      },
    );
  });

  const onMemberSelected = usePersistFn(async (assignTo: OrgMember) => {
    if (assignTo.id === flow?.assignedTo.id) {
      return;
    }
    if (
      storeConfiguration.restrictedRolesForUpdateInspectionFlowAssignment
        ?.length
    ) {
      const roleHelper = new OrgUserRoleHelper(profile.user.role);
      if (
        !roleHelper.matchesAny(
          storeConfiguration.restrictedRolesForUpdateInspectionFlowAssignment,
        )
      ) {
        void alert({
          title: '您没有权限修改当前检测派工',
          message: '若需帮助，请联系门店系统管理员！',
        });
        setIsMemberListVisible(false);
        return;
      }
    }
    await appLoading.showWithAction(async () => {
      await taskManager.inspectionManager.flowManager.updateInspectionFlowAssignee(
        flowId,
        assignTo.id,
      );
      setIsMemberListVisible(false);
    });
  });

  const onTechnicianPress = useCallback(() => {
    setIsMemberListVisible(true);
  }, []);

  useEffect(() => {
    void taskManager.sync();
  }, [taskManager]);

  return {
    flow,
    user,
    listRef,
    scrollY,
    sections,
    scrollerItems,
    siteInspectionFlow,
    keyword,
    onKeywordChange,
    onTechnicianPress,
    onMemberSelected,
    onMorePress,
    isMemberListVisible,
    setIsMemberListVisible,
    activeSectionForHiddenSites,
    setActiveSectionForHiddenSites,
    onSiteSelected,
    onSiteDismissed,
    onHiddenSiteSelected,
    onScrollerItemPress,
    onSearchInputPress,
    onScroll,
    onPreview,
  };
};
